import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AuthPromptDialog from '../components/auth/AuthPromptDialog';
import { CART_CHECKOUT_PATH } from '../constants/cartRoutes';
import type { User } from '../types';
import { generateAriaLabel, getAnnouncementProps } from '../utils/aria';
import { canAccessAdmin } from '../utils/auth';
import {
  type PendingSignInAction,
  consumePendingSignInAction,
  discardPendingSignInAction,
  pendingSignInActionFor,
} from '../utils/pendingSignInAction';
import { consumePostLoginRedirect, isAuthFlowPath } from '../utils/postLoginRedirect';
import { useAuth } from './AuthContext';
import { useWishlist } from './WishlistContext';

export type AuthPromptTab = 'logIn' | 'signUp';

export interface AuthPromptWishlistItem {
  kind: 'accommodation' | 'service';
  id: number;
  name?: string;
  imageUrl?: string;
}

export type AuthPromptEntry = 'login' | 'register' | 'forgotPassword';

export type AuthPromptRequest =
  | {
      intent: 'signIn';
      initialTab?: AuthPromptTab;
      reason?: 'sessionExpired';
      entry?: AuthPromptEntry;
    }
  | { intent: 'wishlist'; item: AuthPromptWishlistItem }
  | { intent: 'checkout' };

interface AuthPromptContextValue {
  openAuthPrompt: (request: AuthPromptRequest) => void;
  /** A protected page holds the pop-up open while it shows; returns the release function. */
  holdForProtectedPage: () => () => void;
}

interface OpenPrompt {
  /** Changes on every open, so a replacing request starts from a fresh dialog. */
  openCount: number;
  request: AuthPromptRequest;
}

// Entries whose admins land on the admin dashboard when nothing was captured.
const ADMIN_LANDING_ENTRIES: readonly AuthPromptEntry[] = ['login', 'forgotPassword'];
const DEFAULT_TAB: AuthPromptTab = 'logIn';
const ANNOUNCEMENT_CLEAR_DELAY_MS = 2000;

const AuthPromptContext = createContext<AuthPromptContextValue | undefined>(undefined);

export const useAuthPrompt = (): AuthPromptContextValue => {
  const context = useContext(AuthPromptContext);
  if (context === undefined) {
    throw new Error('useAuthPrompt must be used within an AuthPromptProvider');
  }
  return context;
};

const RouteContentKeyContext = createContext(0);

/** Wraps the route content so it remounts after a sign-in from the expired pop-up. */
export const KeyedRouteContent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <React.Fragment key={useContext(RouteContentKeyContext)}>{children}</React.Fragment>
);

/** Owns the open sign-in request and renders the pop-up host. Must sit inside the router. */
export const AuthPromptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, sessionExpiredCount } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { ensureInWishlist } = useWishlist();
  const [openPrompt, setOpenPrompt] = useState<OpenPrompt | null>(null);
  // The tab the guest last switched to this visit; provider memory only, never stored.
  const [rememberedTab, setRememberedTab] = useState<AuthPromptTab | null>(null);
  // A guest signed in from the pop-up and the action is waiting for the session to be visible.
  const [pendingAction, setPendingAction] = useState<PendingSignInAction | null>(null);
  const [announcement, setAnnouncement] = useState('');
  // Bumped when a guest signs in from the expired pop-up, so the page behind it loads afresh.
  const [routeContentKey, setRouteContentKey] = useState(0);
  const openCountRef = useRef(0);
  const hasReadStoredActionRef = useRef(false);
  // The open count of the pop-up that is on screen now; null once it is closed. A sign-in
  // that resolves after the guest closed the pop-up reports a count that no longer matches.
  const activeCountRef = useRef<number | null>(null);
  // Protected pages currently holding the pop-up open; a dismiss while any hold is active goes home.
  const holdCountRef = useRef(0);
  // Starts at the count the provider mounted with: only a rise after mount is an expiry.
  const handledExpiryCountRef = useRef(sessionExpiredCount);

  const openAuthPrompt = useCallback((request: AuthPromptRequest) => {
    openCountRef.current += 1;
    activeCountRef.current = openCountRef.current;
    setPendingAction(null);
    discardPendingSignInAction();
    setOpenPrompt({ openCount: openCountRef.current, request });
  }, []);

  const closeAuthPrompt = useCallback(() => {
    activeCountRef.current = null;
    setOpenPrompt(null);
  }, []);

  // A close after sign-in is not a dismiss. A dismiss while a protected page holds the pop-up
  // sends the guest home, because the page behind it is not theirs to see.
  const dismissAuthPrompt = useCallback(() => {
    closeAuthPrompt();
    if (holdCountRef.current > 0) navigate('/', { replace: true });
  }, [closeAuthPrompt, navigate]);

  const holdForProtectedPage = useCallback(() => {
    holdCountRef.current += 1;
    // The count of the pop-up this hold opened; null when it found one already open.
    let openedCount: number | null = null;
    if (activeCountRef.current === null) {
      openAuthPrompt({ intent: 'signIn' });
      openedCount = activeCountRef.current;
    }
    let isReleased = false;
    return () => {
      if (isReleased) return;
      isReleased = true;
      holdCountRef.current -= 1;
      // A page that goes away (sign-out, account deletion) takes its own pop-up with it.
      if (openedCount !== null && activeCountRef.current === openedCount) closeAuthPrompt();
    };
  }, [openAuthPrompt, closeAuthPrompt]);

  const handleSignedIn = (openCount: number, request: AuthPromptRequest, user: User | null) => {
    if (activeCountRef.current !== openCount) return;
    closeAuthPrompt();
    if (request.intent === 'signIn' && request.reason === 'sessionExpired') {
      setRouteContentKey((key) => key + 1);
    }
    const action = pendingSignInActionFor(request);
    if (action) setPendingAction(action);
    // Only a pop-up opened by an auth route lands anywhere after sign-in.
    if (request.intent === 'signIn' && request.entry) {
      const returnTo = consumePostLoginRedirect();
      if (returnTo) navigate(returnTo);
      else if (ADMIN_LANDING_ENTRIES.includes(request.entry) && canAccessAdmin(user)) {
        navigate('/admin');
      }
    }
  };

  // The session ended: drop any action waiting on it and, off the auth-flow pages, ask the guest
  // to log in again. A pop-up that is already asking keeps what was typed into it. A plain
  // pop-up opened in the same commit (a protected page's) is replaced so the notice shows.
  useEffect(() => {
    if (sessionExpiredCount <= handledExpiryCountRef.current) return;
    handledExpiryCountRef.current = sessionExpiredCount;
    setPendingAction(null);
    if (isAuthFlowPath(pathname)) return;
    const isExpiredPromptOpen =
      openPrompt?.request.intent === 'signIn' && openPrompt.request.reason === 'sessionExpired';
    if (isExpiredPromptOpen) return;
    openAuthPrompt({ intent: 'signIn', initialTab: 'logIn', reason: 'sessionExpired' });
  }, [sessionExpiredCount, pathname, openPrompt, openAuthPrompt]);

  // A guest who left for Google comes back to a fresh page load with the action in storage.
  // It is read once, after the sign-in bootstrap, and never on an auth-flow page such as the
  // callback, which has not finished signing the guest in. A load that ends signed out drops it.
  useEffect(() => {
    if (hasReadStoredActionRef.current || isLoading || isAuthFlowPath(pathname)) return;
    hasReadStoredActionRef.current = true;
    const storedAction = consumePendingSignInAction();
    if (storedAction && isAuthenticated) setPendingAction(storedAction);
  }, [isLoading, isAuthenticated, pathname]);

  // The action runs from an effect, not the submit handler: the sign-in resolves before the
  // session flag flips, and the wishlist functions read that flag from their render. Checkout
  // also waits for loading to finish, as the route guard on the checkout page does.
  useEffect(() => {
    if (!pendingAction || !isAuthenticated) return;
    if (pendingAction.kind === 'checkout') {
      if (isLoading) return;
      setPendingAction(null);
      navigate(CART_CHECKOUT_PATH);
      return;
    }
    const { listing } = pendingAction;
    setPendingAction(null);
    void ensureInWishlist({ kind: listing.kind, id: listing.id }).then((saved) => {
      if (saved) setAnnouncement(generateAriaLabel.wishlist.added(listing.kind, listing.name));
    });
  }, [pendingAction, isAuthenticated, isLoading, ensureInWishlist, navigate]);

  useEffect(() => {
    if (!announcement) return undefined;
    const timer = setTimeout(() => setAnnouncement(''), ANNOUNCEMENT_CLEAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, [announcement]);

  const contextValue = useMemo(
    () => ({ openAuthPrompt, holdForProtectedPage }),
    [openAuthPrompt, holdForProtectedPage],
  );

  return (
    <AuthPromptContext.Provider value={contextValue}>
      <RouteContentKeyContext.Provider value={routeContentKey}>
        {children}
      </RouteContentKeyContext.Provider>
      {openPrompt && (
        <AuthPromptDialog
          key={openPrompt.openCount}
          request={openPrompt.request}
          initialTab={
            (openPrompt.request.intent === 'signIn' ? openPrompt.request.initialTab : undefined) ??
            rememberedTab ??
            DEFAULT_TAB
          }
          onTabChange={setRememberedTab}
          onSignedIn={(user) => handleSignedIn(openPrompt.openCount, openPrompt.request, user)}
          onClose={dismissAuthPrompt}
        />
      )}
      <div className="sr-only" {...getAnnouncementProps(announcement)}>
        {announcement}
      </div>
    </AuthPromptContext.Provider>
  );
};
